import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Zap, MapPin, BookOpen, ShieldCheck, Lock, CreditCard, Star } from 'lucide-react';
import { Button } from './Button';
export function Hero() {
  return <section className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden bg-blue-900 text-white" data-id="element-99">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-100">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-amber-500 via-transparent to-transparent" data-id="element-101" />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-102">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-103">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-800/50 border border-blue-700/50 text-blue-100 mb-8 text-sm font-medium" data-id="element-104">
            <span className="text-amber-400" data-id="element-105">⭐</span> El sistema N°1 de
            estimulación cognitiva para adultos mayores en Argentina
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-106">
            <span className="text-amber-400" data-id="element-107">🧠 MEGAPACK MENTE VIVA™</span>
            <br data-id="element-108" />
            Todo un año de estimulación cognitiva.
            <br data-id="element-109" />
            Listo. Desde hoy.
          </h1>

          <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl mx-auto font-medium" data-id="element-110">
            Actividades + seguimiento + bonos listos para usar. Sin buscar. Sin
            improvisar. Sin perder tiempo.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.15
      }} className="relative mb-10" data-id="element-111">
          {/* Warm gold glow that harmonizes with the product's gold tones */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-112">
            <div className="w-72 h-72 md:w-96 md:h-96 bg-amber-400/30 rounded-full blur-3xl" data-id="element-113" />
          </div>
          <motion.div animate={{
          y: [0, -12, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative mx-auto max-w-2xl" data-id="element-114">
            <img src="https://cdn.magicpatterns.com/uploads/s9TzaxwyN9bqNtj9G35a1E/PNG.png" alt="Megapack Mente Viva Gold Edition — 6 Tomos y 6 Bonos para adultos mayores" className="w-full drop-shadow-2xl" data-id="element-115" />
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="flex flex-wrap justify-center gap-4 mb-12" data-id="element-116">
          {[{
          icon: FileText,
          text: '100% PDF'
        }, {
          icon: Zap,
          text: 'Acceso en 5 min'
        }, {
          icon: MapPin,
          text: 'Hecho para Argentina'
        }, {
          icon: BookOpen,
          text: '6 Tomos + 6 Bonos'
        }].map((badge, i) => <div key={i} className="flex items-center gap-2 bg-blue-800/40 px-4 py-2 rounded-lg text-blue-50" data-id="element-117">
              <badge.icon className="w-5 h-5 text-amber-400" data-id="element-118" />
              <span className="font-medium" data-id="element-119">{badge.text}</span>
            </div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="flex flex-col items-center" data-id="element-120">
          <Button href="#planes" className="text-xl md:text-2xl px-8 py-5 mb-6 w-full md:w-auto animate-bounce-subtle" data-id="element-121">
            🧠 → QUIERO MI MEGAPACK MENTE VIVA AHORA
          </Button>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-blue-200 mb-12" data-id="element-122">
            <div className="flex items-center gap-1" data-id="element-123">
              <ShieldCheck className="w-4 h-4" data-id="element-124" /> Garantía 7 días
            </div>
            <div className="flex items-center gap-1" data-id="element-125">
              <Lock className="w-4 h-4" data-id="element-126" /> Pago seguro
            </div>
            <div className="flex items-center gap-1" data-id="element-127">
              <CreditCard className="w-4 h-4" data-id="element-128" /> Mercado Pago
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats Strip */}
      <div className="absolute bottom-0 left-0 w-full bg-blue-950/50 border-t border-blue-800/50 py-4" data-id="element-129">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center md:justify-between items-center gap-4 text-blue-100 text-sm md:text-base font-medium" data-id="element-130">
          <div className="flex items-center gap-2" data-id="element-131">
            👥 +1.000 familias confían
          </div>
          <div className="hidden md:block w-1 h-1 rounded-full bg-blue-700" data-id="element-132"></div>
          <div className="flex items-center gap-2" data-id="element-133">📚 6 Tomos listos</div>
          <div className="hidden md:block w-1 h-1 rounded-full bg-blue-700" data-id="element-134"></div>
          <div className="flex items-center gap-2" data-id="element-135">🎁 6 Bonos incluidos</div>
          <div className="hidden md:block w-1 h-1 rounded-full bg-blue-700" data-id="element-136"></div>
          <div className="flex items-center gap-1 text-amber-400" data-id="element-137">
            4.9/5 <Star className="w-4 h-4 fill-current" data-id="element-138" />
          </div>
        </div>
      </div>
    </section>;
}